/**
 * The canvas chrome: Stage fills its parent with the canvas ground
 * (the drawing surface, never the page ground); Overlay is a glass
 * panel floating over it at one of five anchors; Frame is a full-page
 * scrolling region for the screens that are not a canvas.
 */
import type { ReactNode } from 'react';
import { FRAME, PAGE_COLUMN, RADIUS, SPACE, Z, usePalette } from '../theme';
import { panelStyle } from './panel';

export type Anchor = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'top-center';

const INSET = 16;

function anchorStyle(anchor: Anchor) {
  switch (anchor) {
    case 'top-left':
      return { top: INSET, left: INSET };
    case 'top-right':
      return { top: INSET, right: INSET };
    case 'bottom-left':
      return { bottom: INSET, left: INSET };
    case 'bottom-right':
      return { bottom: INSET, right: INSET };
    case 'top-center':
      return { top: INSET, left: '50%', transform: 'translateX(-50%)' };
  }
}

export function Overlay({
  anchor,
  children,
  width,
  maxHeight,
  ariaLabel,
  dense = false,
}: {
  anchor: Anchor;
  children: ReactNode;
  width?: number;
  maxHeight?: string;
  ariaLabel?: string;
  /** Tighter padding for a strip of controls. */
  dense?: boolean;
}) {
  const p = usePalette();
  return (
    <div
      aria-label={ariaLabel}
      style={{
        ...panelStyle(p, 'glass'),
        position: 'absolute',
        zIndex: Z.overlay,
        padding: dense ? `${SPACE.sm}px ${SPACE.md}px` : `${SPACE.md}px ${SPACE.lg}px`,
        width,
        maxWidth: `calc(100% - ${INSET * 2}px)`,
        maxHeight,
        overflowY: maxHeight ? 'auto' : undefined,
        display: 'flex',
        flexDirection: 'column',
        gap: SPACE.sm,
        ...anchorStyle(anchor),
      }}
    >
      {children}
    </div>
  );
}

/** A column of overlays down one side, each scrolling within its share. */
export function OverlayColumn({ anchor, children, width = 300 }: { anchor: 'top-right' | 'top-left'; children: ReactNode; width?: number }) {
  return (
    <div
      style={{
        position: 'absolute',
        zIndex: Z.overlay,
        display: 'flex',
        flexDirection: 'column',
        gap: SPACE.md,
        width,
        maxHeight: `calc(100% - ${INSET * 2 + FRAME.overlayColumnChrome}px)`,
        ...anchorStyle(anchor),
      }}
    >
      {children}
    </div>
  );
}

/** One glass block inside an OverlayColumn. */
export function OverlayBlock({ children, ariaLabel }: { children: ReactNode; ariaLabel?: string }) {
  const p = usePalette();
  return (
    <div
      aria-label={ariaLabel}
      style={{
        ...panelStyle(p, 'glass'),
        padding: `${SPACE.md}px ${SPACE.lg}px`,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: SPACE.sm,
        overflowY: 'auto',
      }}
    >
      {children}
    </div>
  );
}

export function Stage({ children }: { children: ReactNode }) {
  const p = usePalette();
  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 0, background: p.canvasGround, overflow: 'hidden' }}>{children}</div>
  );
}

/** A whole-page scrolling screen (the overview, the takeoff, the onboarding). */
export function Frame({ children }: { children: ReactNode }) {
  const p = usePalette();
  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        padding: SPACE.xxl,
        boxSizing: 'border-box',
        background: p.surface,
        color: p.ink,
        display: 'flex',
        flexDirection: 'column',
        gap: SPACE.lg,
      }}
    >
      {children}
    </div>
  );
}

/** The workspace frame: a fixed rail beside a canvas that fills the rest. */
export function Workbench({ rail, children }: { rail: ReactNode; children: ReactNode }) {
  const p = usePalette();
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', background: p.surface, color: p.ink }}>
      <aside
        style={{
          width: FRAME.sidebar,
          flexShrink: 0,
          height: '100%',
          padding: SPACE.xl,
          overflowY: 'auto',
          boxSizing: 'border-box',
          borderRight: `1px solid ${p.hairline}`,
          background: p.surfaceGlass,
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          display: 'flex',
          flexDirection: 'column',
          gap: SPACE.sm,
        }}
      >
        {rail}
      </aside>
      <main style={{ position: 'relative', flex: 1, minWidth: 0, borderRadius: RADIUS.sm }}>{children}</main>
    </div>
  );
}

/** The viewport's top-right corner, above everything: where the mode
 * toggle lives so it reaches the screens that are only a centred card. */
export function Corner({ children }: { children: ReactNode }) {
  return <div style={{ position: 'fixed', top: 12, right: 12, zIndex: Z.toggle }}>{children}</div>;
}

/** The Review cockpit's frame (Figma 00a): the header band over a body that scrolls on its own. */
export function Shell({ bar, children }: { bar: ReactNode; children: ReactNode }) {
  const p = usePalette();
  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: p.surface, color: p.ink }}>
      {bar}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>{children}</div>
    </div>
  );
}

/** A screen's page inside the shell: padded, and (with `column`) held to the Figma's centred 1080px column. */
export function Page({ column = false, children }: { column?: boolean; children: ReactNode }) {
  return (
    <div
      style={{
        width: column ? PAGE_COLUMN.width : undefined,
        maxWidth: '100%',
        margin: column ? '0 auto' : undefined,
        padding: column ? `${PAGE_COLUMN.padY}px ${PAGE_COLUMN.padX}px` : SPACE.xxl,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: PAGE_COLUMN.gap,
      }}
    >
      {children}
    </div>
  );
}

/** Tiles in rows of the Figma's fixed width, wrapping with its gap. */
export function Tiles({ children }: { children: ReactNode }) {
  return <div role="list" style={{ display: 'flex', flexWrap: 'wrap', gap: PAGE_COLUMN.tileGap, alignItems: 'flex-start' }}>{children}</div>;
}
